import { Routes } from '@angular/router';
import { LayoutComponent } from './core/layout/layout.component';
import { RoutesEnum, RoutesParamsEnum, RoutesRankingEnum, RoutesTournamentEnum } from './shared/models/routes.enum';
import { AuthGuard } from './core/guards/auth.guard';
import { ERankingType } from './features/ranking/models/ranking-type.interface';

export const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: RoutesEnum.Tournaments,
        pathMatch: 'full',
      },
      {
        path: RoutesEnum.Login,
        loadComponent: () => import('./core/auth/pages/login/login.component').then(c => c.LoginComponent),
      },
      {
        path: RoutesEnum.Register,
        loadComponent: () => import('./core/auth/pages/register/register.component').then(c => c.RegisterComponent),
      },
      {
        path: RoutesEnum.RemindPassword,
        loadComponent: () =>
          import('./features/forgot-password/forgot-password.component').then(c => c.ForgotPasswordComponent),
      },
      {
        path: RoutesEnum.ResetPassword,
        loadComponent: () =>
          import('./features/reset-password/reset-password.component').then(c => c.ResetPasswordComponent),
      },
      {
        path: RoutesEnum.MyAccount,
        canActivate: [AuthGuard],
        loadComponent: () => import('./features/my-account/my-account.component').then(c => c.MyAccountComponent),
        children: [
          {
            path: '',
            redirectTo: RoutesEnum.MyProfile,
            pathMatch: 'full',
          },
          {
            path: RoutesEnum.ProfileComplete,
            loadComponent: () =>
              import('./features/my-account/pages/profile-complete/profile-complete.component').then(
                c => c.ProfileCompleteComponent,
              ),
          },
          {
            path: RoutesEnum.ProfileReady,
            data: { page: RoutesEnum.ProfileReady },
            loadComponent: () =>
              import('./features/my-account/pages/congratulations/congratulations.component').then(
                c => c.CongratulationsComponent,
              ),
          },
          {
            path: RoutesEnum.EditProfile,
            loadComponent: () =>
              import('./features/my-account/pages/edit-profile/edit-profile.component').then(
                c => c.EditProfileComponent,
              ),
          },
          {
            path: RoutesEnum.Licence,
            loadComponent: () =>
              import('./features/my-account/pages/licence-activate/licence-activate.component').then(
                c => c.LicenceActivateComponent,
              ),
          },
          {
            path: RoutesEnum.ThankYou,
            data: { page: RoutesEnum.ThankYou },
            loadComponent: () =>
              import('./features/my-account/pages/congratulations/congratulations.component').then(
                c => c.CongratulationsComponent,
              ),
          },
          {
            path: RoutesEnum.ChangePassword,
            loadComponent: () =>
              import('./features/my-account/pages/change-password/change-password.component').then(
                c => c.ChangePasswordComponent,
              ),
          },
          {
            path: RoutesEnum.MyProfile,
            loadComponent: () =>
              import('./features/my-account/pages/my-profile/my-profile.component').then(c => c.MyProfileComponent),
            children: [
              {
                path: '',
                redirectTo: RoutesEnum.PlayerResults,
                pathMatch: 'full',
              },
              {
                path: RoutesEnum.PlayerResults,
                loadComponent: () =>
                  import('./features/my-account/pages/my-results/my-results.component').then(c => c.MyResultsComponent),
              },
              {
                path: RoutesEnum.PlayerScheduledMatches,
                loadComponent: () =>
                  import('./features/my-account/pages/my-scheduled-matches/my-scheduled-matches.component').then(
                    c => c.MyScheduledMatchesComponent,
                  ),
              },
              {
                path: RoutesEnum.PlayerWideos,
                loadComponent: () =>
                  import('./features/players/pages/player-wideos/player-wideos.component').then(
                    c => c.PlayerWideosComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: RoutesEnum.Players,
        children: [
          {
            path: ':slug',
            loadComponent: () =>
              import('./features/players/pages/player-profile/player-profile.component').then(
                c => c.PlayerProfileComponent,
              ),
            children: [
              {
                path: '',
                redirectTo: RoutesEnum.PlayerResults,
                pathMatch: 'full',
              },
              {
                path: RoutesEnum.PlayerResults,
                loadComponent: () =>
                  import('./features/players/pages/player-results/player-results.component').then(
                    c => c.PlayerResultsComponent,
                  ),
              },
              {
                path: RoutesEnum.PlayerScheduledMatches,
                loadComponent: () =>
                  import('./features/players/pages/player-scheduled-matches/player-scheduled-matches.component').then(
                    c => c.PlayerScheduledMatchesComponent,
                  ),
              },
              {
                path: RoutesEnum.PlayerWideos,
                loadComponent: () =>
                  import('./features/players/pages/player-wideos/player-wideos.component').then(
                    c => c.PlayerWideosComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: RoutesEnum.Ranking,
        loadComponent: () => import('./features/ranking/pages/ranking/ranking.component').then(c => c.RankingComponent),
        children: [
          {
            path: '',
            redirectTo: RoutesRankingEnum.General,
            pathMatch: 'full',
          },
          {
            path: RoutesRankingEnum.General,
            loadComponent: () =>
              import('./features/ranking/pages/ranking-details/ranking-details.component').then(
                c => c.RankingDetailsComponent,
              ),
          },
          {
            path: RoutesRankingEnum.Men,
            data: { type: ERankingType.Man },
            loadComponent: () =>
              import('./features/ranking/pages/ranking-details/ranking-details.component').then(
                c => c.RankingDetailsComponent,
              ),
          },
          {
            path: RoutesRankingEnum.Women,
            data: { type: ERankingType.Woman },
            loadComponent: () =>
              import('./features/ranking/pages/ranking-details/ranking-details.component').then(
                c => c.RankingDetailsComponent,
              ),
          },
        ],
      },
      {
        path: RoutesEnum.Tournaments,
        children: [
          {
            path: '',
            redirectTo: RoutesEnum.List,
            pathMatch: 'full',
          },
          {
            path: RoutesEnum.List,
            loadComponent: () =>
              import('./features/tournaments/pages/tournaments-list/tournaments-list.component').then(
                c => c.TournamentsListComponent,
              ),
          },
        ],
      },
      {
        path: `:${RoutesParamsEnum.clubSlug}`,
        children: [
          {
            path: RoutesEnum.Tournaments,
            children: [
              {
                path: `:${RoutesParamsEnum.tournamentSlug}/:${RoutesParamsEnum.tournamentDate}`,
                loadComponent: () =>
                  import('./features/tournaments/pages/tournament-details/tournament-details.component').then(
                    c => c.TournamentDetailsComponent,
                  ),
                children: [
                  {
                    path: '',
                    redirectTo: RoutesTournamentEnum.Information,
                    pathMatch: 'full',
                  },
                  {
                    path: RoutesTournamentEnum.Information,
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-info/tournament-info.component').then(
                        c => c.TournamentInfoComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.Members,
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-members/tournament-members.component').then(
                        c => c.TournamentMembersComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.YouAreRegistered,
                    data: { isThankYou: true },
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-members/tournament-members.component').then(
                        c => c.TournamentMembersComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.TakePart,
                    canActivate: [AuthGuard],
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-take-part/tournament-take-part.component').then(
                        c => c.TournamentTakePartComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.Results,
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-results/tournament-results.component').then(
                        c => c.TournamentResultsComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.Table,
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-table/tournament-table.component').then(
                        c => c.TournamentTableComponent,
                      ),
                  },
                  {
                    path: RoutesTournamentEnum.CupPhase,
                    loadComponent: () =>
                      import('./features/tournaments/pages/tournament-cup-phase/tournament-cup-phase.component').then(
                        c => c.TournamentCupPhaseComponent,
                      ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
